// ---------------------------------------------------------------------------
// Tier assignment – composite scoring (shared between frontend and scripts)
// ---------------------------------------------------------------------------
//
// This module is the single source of truth for tier computation.
// Frontend: imported via $lib/utils/tiers (re-exported from stats.ts)
// Scripts:  imported via relative path (../../src/lib/utils/tiers.ts)
//
// Keep this file free of SvelteKit-specific imports ($lib, $app, etc.)
// so it can be consumed by both environments.
// ---------------------------------------------------------------------------

/**
 * Minimal input shape for tier assignment.
 * Any object with winRate + pickRate satisfies this. banRate is optional
 * — pre-rollout snapshots and Quick Play omit it and contribute 0 demand.
 */
export interface TierInput {
    winRate: number;
    pickRate: number;
    banRate?: number | null;
}

/**
 * Output fields added by assignTiers.
 */
export interface TierResult {
    tier: string;
    tierScore: number;
}

/** Sentinel tier for heroes with insufficient data from Blizzard. */
export const INSUFFICIENT_DATA_TIER = "\u2014";

/**
 * Weight given to the demand term when computing the composite tier score.
 *
 * Demand combines pick rate and ban rate — both are "match-slot demand"
 * signals: a hero either gets picked or gets removed by the opponent.
 *
 * Intentionally moderate so win rate remains the dominant factor. The
 * demand term is one-sided (above-average demand boosts; below-average
 * doesn't penalize), so this weight can never drag a hero down — it only
 * nudges popular-and/or-banned heroes upward.
 *
 * Legacy back-compat: when banRate is null/missing everywhere (pre-ban-rate
 * snapshots, Quick Play), `z(PR + 0) === z(PR)` and the formula collapses
 * to the previous PR-only behavior.
 */
export const DEMAND_WEIGHT = 0.2;

/**
 * Backwards-compatible alias for code paths that still reference the old name.
 * Removed once all callers migrate.
 * @deprecated use DEMAND_WEIGHT
 */
export const PICK_RATE_WEIGHT = DEMAND_WEIGHT;

/**
 * Assign tier letters (S / A / B / C / D) to a set of heroes using a
 * composite z-score built from win rate and combined demand (PR + BR).
 *
 * Heroes whose stats are Blizzard's "insufficient data" sentinel (negative
 * values) are assigned the "—" tier and excluded from scoring.
 *
 * Demand uses asymmetric (one-sided) influence: above-average demand gives
 * a small boost; below-average demand contributes zero (protects heroes
 * that are simply off-meta).
 *
 * Tier thresholds use σ-buckets on the composite score distribution:
 *   S: > μ + 1.5σ   A: > μ + 0.5σ   B: > μ − 0.5σ   C: > μ − 1.5σ   D: rest
 */
export function assignTiers<T extends TierInput>(heroes: T[]): (T & TierResult)[] {
    if (heroes.length === 0) return [];

    // Separate heroes with valid data from those with insufficient data
    const validHeroes: T[] = [];
    const insufficientHeroes: T[] = [];
    for (const h of heroes) {
        if (h.winRate < 0 || h.pickRate < 0) {
            insufficientHeroes.push(h);
        } else {
            validHeroes.push(h);
        }
    }

    // Assign insufficient-data heroes a placeholder tier
    const insufficientResults: (T & TierResult)[] = insufficientHeroes.map((hero) => ({
        ...hero,
        tier: INSUFFICIENT_DATA_TIER,
        tierScore: -Infinity,
    }));

    if (validHeroes.length === 0) return insufficientResults;

    // Win-rate statistics
    const winRates = validHeroes.map((h) => h.winRate);
    const wrMean = winRates.reduce((a, b) => a + b, 0) / winRates.length;
    const wrStdDev = Math.sqrt(winRates.reduce((sum, wr) => sum + (wr - wrMean) ** 2, 0) / winRates.length);

    if (wrStdDev === 0) {
        return [...validHeroes.map((hero) => ({ ...hero, tier: "B", tierScore: 0 })), ...insufficientResults];
    }

    // Demand statistics — combine pick rate and (clamped) ban rate per hero.
    // null/negative banRate (insufficient-data sentinel or missing) contribute 0
    // to the sum, which matches "no ban data → behave like the legacy formula".
    const demand = validHeroes.map((h) => {
        const br = h.banRate;
        return h.pickRate + (br === null || br === undefined || br < 0 ? 0 : br);
    });
    const dMean = demand.reduce((a, b) => a + b, 0) / demand.length;
    const dStdDev = Math.sqrt(demand.reduce((sum, d) => sum + (d - dMean) ** 2, 0) / demand.length);
    const useDemand = dStdDev > 0;

    // Composite scores
    const scores = validHeroes.map((hero, i) => {
        const zWR = (hero.winRate - wrMean) / wrStdDev;
        const zDemand = useDemand ? Math.max(0, (demand[i] - dMean) / dStdDev) : 0;
        return zWR + DEMAND_WEIGHT * zDemand;
    });

    // Score statistics for tier bucketing
    const scoreMean = scores.reduce((a, b) => a + b, 0) / scores.length;
    const scoreStdDev = Math.sqrt(scores.reduce((sum, s) => sum + (s - scoreMean) ** 2, 0) / scores.length);

    const tieredValid = validHeroes.map((hero, i) => {
        const diff = scores[i] - scoreMean;
        let tier: string;
        if (scoreStdDev === 0) tier = "B";
        else if (diff > 1.5 * scoreStdDev) tier = "S";
        else if (diff > 0.5 * scoreStdDev) tier = "A";
        else if (diff > -0.5 * scoreStdDev) tier = "B";
        else if (diff > -1.5 * scoreStdDev) tier = "C";
        else tier = "D";

        return { ...hero, tier, tierScore: scores[i] };
    });

    return [...tieredValid, ...insufficientResults];
}

// ---------------------------------------------------------------------------
// Patch trend types (shared between frontend and scripts)
// ---------------------------------------------------------------------------

/** Per-hero WR/PR/BR deltas and tier change for a specific patch. */
export interface PatchHeroTrend {
    /** Win rate delta (signed, e.g. +0.023 = +2.3pp) */
    wr: number;
    /** Pick rate delta (signed) */
    pr: number;
    /**
     * Ban rate delta (signed). Omitted when ban-rate data is missing from
     * either the comparison anchor or the current snapshot (e.g. patches
     * predating ban-rate tracking).
     */
    br?: number;
    /** Tier before the patch (e.g. "B") */
    tierBefore: string;
    /** Tier after the patch (e.g. "S") */
    tierAfter: string;
    /** Hero had no meaningful data before this patch (brand new hero). */
    isNew?: boolean;
}

/**
 * Shape of static/history/patch-trends-*.json
 *
 * Outer key = patch date (YYYY-MM-DD)
 * Middle key = tier name or "quickplay"
 * Inner key = hero slug → trend data
 */
export type PatchTrendsData = Record<string, Record<string, Record<string, PatchHeroTrend>>>;
